import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Stack, Tooltip, Typography } from "@mui/material";

import { DocumentTransformation, TransformationRowDecisionsPayload } from "@/types/documentTransformation";

/**
 * Reads the row decisions off a transformation.
 *
 * A ONE-LINE FUNCTION WITH A TEST, because the one thing that can silently
 * break here is the PATH. #6921 first wrote these under
 * `record_summary.row_decisions` and !4681 review moved them up to
 * `processing_metadata.row_decisions` — the nested spot was rewritten
 * wholesale by the worker's SummarizeRecords and did not exist on the failure
 * path at all.
 *
 * Inline in the tab's JSX the path was unassertable without rendering the
 * whole review tab, so a repeat of that move would land with every test green
 * and the notice simply gone. Reading the wrong path returns undefined, which
 * is a legal value meaning "not measured", so nothing complains.
 */
export const selectRowDecisions = (
  // Accepts null as well as undefined: the tab's latestTransformation is
  // `DocumentTransformation | null`, and both mean "no transformation to read".
  transformation: DocumentTransformation | null | undefined
): TransformationRowDecisionsPayload | undefined => transformation?.processing_metadata?.row_decisions;

/**
 * What the extractor did to the reviewer's rows, and did not tell them (#6921).
 *
 * Two decisions during transformation change what will load. Both were already
 * counted in code. Both went **only to a log line** — `git grep` for
 * `skippedAggregateRows` and `forwardFilledRows` returned the transformer and
 * nothing else. So a reviewer approving a document had no way to know either
 * happened.
 *
 * A reviewer approves believing the grid is what will load. These two make it
 * differ from the file — in row COUNT and in ATTRIBUTION — with nothing on
 * screen saying so.
 *
 * ## Disclosure, not behaviour
 *
 * Neither heuristic changes. Both are load-bearing and were each fixed
 * deliberately (#5727, #6487); changing them is a separate decision with its
 * own evidence bar. This only says what happened.
 *
 * ## Why not blocking
 *
 * Because both are usually right. A carrier file genuinely does interleave
 * subtotal rows, and a claimant block genuinely does leave continuation rows
 * blank. Blocking approval on the normal case would make the reviewer dismiss
 * it every time, which is how a real signal becomes furniture. The precedent is
 * the search box, which discloses its scope rather than refusing to search.
 */
export interface RowDecisionsNoticeProps {
  /**
   * The transformer's counts, or undefined when it did not report them — every
   * transformation finalized before #6921.
   *
   * `undefined` and `{0, 0}` both render nothing, for OPPOSITE reasons: the
   * first is "nobody measured", the second is "measured, neither fired". They
   * are distinguished here rather than collapsed, because a future surface that
   * wants to say "this document was processed before we tracked this" needs the
   * difference and cannot recover it once merged.
   */
  decisions?: TransformationRowDecisionsPayload;
}

export const RowDecisionsNotice = ({ decisions }: RowDecisionsNoticeProps) => {
  // Not measured. Nothing honest to say.
  if (!decisions) return null;

  const { skipped_aggregate_rows: skipped, forward_filled_rows: filled } = decisions;

  // Measured, and neither heuristic fired. Also nothing to say — and this is
  // the overwhelming majority of documents, so an empty state here would be
  // permanent furniture.
  if (skipped <= 0 && filled <= 0) return null;

  // Assembled as fragments rather than two block lines. This is ambient
  // context, not a problem to solve, so it gets one quiet row -- the blocking
  // errors banner is the only thing on this surface that earns an Alert.
  // Two stacked full-width alerts gave equal visual weight to a blocking
  // problem and a footnote, and read as a wall above the grid.
  const parts: string[] = [];
  if (skipped > 0) {
    parts.push(`${skipped.toLocaleString()} ${skipped === 1 ? "row" : "rows"} skipped as subtotals (not loaded)`);
  }
  if (filled > 0) {
    parts.push(`${filled.toLocaleString()} ${filled === 1 ? "row" : "rows"} took a claimant from the row above`);
  }

  return (
    <Stack direction="row" spacing={0.75} sx={{ px: 2.5, pt: 1.5, alignItems: "flex-start", color: "text.secondary" }}>
      <InfoOutlinedIcon sx={{ fontSize: 16, mt: "1px", flexShrink: 0 }} />
      <Typography variant="caption" sx={{ lineHeight: 1.5 }}>
        {parts.join(" · ")}{" "}
        <Tooltip
          title={
            "Rows with amounts filled and the detail columns blank are treated as subtotal lines and not loaded, " +
            "because loading them double-counts. Rows with blank identity columns take the claimant from the row " +
            "above, as vendors lay out a claimant header followed by its claim lines. Both are usually correct — " +
            "check the source preview if a row count or a claimant name looks wrong."
          }
        >
          <Box component="span" sx={{ textDecoration: "underline dotted", cursor: "help" }}>
            Why?
          </Box>
        </Tooltip>
      </Typography>
    </Stack>
  );
};
